import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StatusBadge from "../components/StatusBadge";
import { toast } from "sonner";
import { Trash2, RefreshCcw, ArrowRight, PackageCheck, Scale, Boxes, Plus, X, Search, UserCheck, Pencil } from "lucide-react";
import { useNavigate } from "react-router-dom";

const money = (v) =>
    Number(v || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const num = (v, d = 2) =>
    Number(v || 0).toLocaleString(undefined, {
        minimumFractionDigits: d,
        maximumFractionDigits: d,
    });

function statusText(line) {
    const status = line.availability_status || line.allocation_status || line.allocation?.status;

    if (status === "available_in_x_batches") {
        return line.tonality_summary || line.allocation?.tonality_summary || `Available in ${line.tonality_count || 0} tonalities`;
    }

    if (status === "not_enough") return "Not enough stock";
    if (status === "without_stock") return "Out of stock";
    if (status === "available") return line.tonality_summary || "Available";

    return "Pending validation";
}

const emptyCustomerForm = {
    company_name: "",
    address: "",
    tin: "",
    contact_person: "",
    contact_no: "",
    email: "",
};

export default function Cart() {
    const { user } = useAuth();
    const isNonOrganic = user?.business_mode === "non_organic";
    const showPrices = user?.price_visibility !== "none";
    const npTier = isNonOrganic ? (user?.price_visibility || "dealers") : "srp2";
    const priceLabel = npTier === "distributor" ? "Distributor" : npTier === "dealers" ? "Dealers" : "SRP";
    const _pos = (...v) => { for (const x of v) { if (Number(x) > 0) return Number(x); } return Number(v[v.length - 1] ?? 0); };
    const linePrice = (l) => npTier === "distributor" ? _pos(l.dealer_price, l.unit_price)
        : npTier === "dealers" ? _pos(l.dealers_price, l.dealer_price, l.unit_price)
        : _pos(l.srp2, l.unit_price);
    const showLinePromo = (l) => isNonOrganic && (npTier === "distributor" || npTier === "dealers") && !!l.promo_price; // UAT 10-28: red promo only for +Promo tiers 2A (distributor) / 2B (dealers)
    const npUnit = (l) => (Number(l.promo_price) > 0 ? Number(l.promo_price) : linePrice(l));
    const npLineTotal = (l) => npUnit(l) * Number(l.quantity || 0);

    const [cart, setCart] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [customer, setCustomer] = useState("");
    const [customerContext, setCustomerContext] = useState(null);
    const [showNewCustomer, setShowNewCustomer] = useState(false);
    const [showNewClient, setShowNewClient] = useState(false);
    const [newClientName, setNewClientName] = useState("");
    const [newCustomer, setNewCustomer] = useState(emptyCustomerForm);
    const [busy, setBusy] = useState(false);
    // Per-item selection for "combine into one quotation". Empty set = convert all lines.
    const [selectedLines, setSelectedLines] = useState(() => new Set());
    const [clientModal, setClientModal] = useState(null); // null | {mode:"new"} | {mode:"edit", id}
    const [poFields, setPoFields] = useState({ sales_agent: "", remarks: "", replacement_po_no: "", po_no: "" });
    // UAT 10-26 #3: add items directly in the cart (search + add) without going back to Inquiry.
    const [addOpen, setAddOpen] = useState(false);
    const [addQ, setAddQ] = useState("");
    const [addResults, setAddResults] = useState([]);
    const [addSearching, setAddSearching] = useState(false);
    const [addBusy, setAddBusy] = useState(false);
    const [addQty, setAddQty] = useState({});
    const [creditModal, setCreditModal] = useState(null);  // UAT 10-28: credit-line choice as a modal
    const [agentOptions, setAgentOptions] = useState([]);
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get("/non-organic/sales-agents");
                if (alive) setAgentOptions((data && data.agents) || []);
            } catch { /* pick list is optional; the field still accepts free text */ }
        })();
        return () => { alive = false; };
    }, []);
    const [lineAgents, setLineAgents] = useState({});  // E50/E83: Sales Agent per row
    const nav = useNavigate();

    const refresh = async (preferredCode = "") => {
        const c = await api.get("/cart/current");

        let ctx = { data: null };
        try {
            ctx = await api.get("/cart/customer-context");
        } catch {
            ctx = { data: null };
        }

        const lines = c.data?.lines || [];
        const schemaName =
            ctx.data?.customer_schema_name ||
            lines[0]?.schema_name ||
            "";

        // Customer list is drawn from the signed-in agent's own CRM leads.
        const cust = await api.get("/cart/lead-customers");
        const leadCustomers = cust.data?.customers || [];

        setCart(c.data);
        setCustomerContext(ctx.data);
        setCustomers(leadCustomers);

        if (leadCustomers.length > 0) {
            setCustomer((s) => {
                if (preferredCode) return preferredCode;
                const stillValid = leadCustomers.some((x) => x.customer_code === s);
                return stillValid ? s : leadCustomers[0].customer_code;
            });
        } else {
            setCustomer("");
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    // The cart's company + warehouse come from its existing lines (every line shares them).
    const cartSchemaId = cart?.lines?.[0]?.schema_id || "";
    const cartWarehouse = cart?.lines?.[0]?.warehouse_code || "";

    const searchAddItems = async (q) => {
        if (!cartSchemaId) { toast.error("Add at least one item from Inquiry first — then the cart knows the company and warehouse."); return; }
        setAddSearching(true);
        try {
            const { data } = await api.get("/inquiry/items/search", { params: { schemaId: cartSchemaId, warehouseCode: cartWarehouse, q } });
            setAddResults(Array.isArray(data) ? data : []);
        } catch (e) { toast.error(formatApiError(e)); }
        setAddSearching(false);
    };

    const addItemToCart = async (item) => {
        const qty = Number(addQty[item.item_code] || 0);
        if (!(qty > 0)) { toast.error("Enter a quantity greater than zero."); return; }
        setAddBusy(true);
        try {
            await api.post("/cart/items", { schema_id: cartSchemaId, warehouse_code: cartWarehouse, item_code: item.item_code, quantity: qty });
            toast.success(`Added ${item.item_code} to the cart`);
            setAddQty((pr) => ({ ...pr, [item.item_code]: "" }));
            await refresh();
        } catch (e) { toast.error(formatApiError(e)); }
        setAddBusy(false);
    };

    const removeLine = async (lineId) => {
        try {
            const { data } = await api.delete(`/cart/items/${lineId}`);
            setCart(data);
            toast.success("Removed from cart");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const updateQuantity = async (lineId, value) => {
        let quantity = Number(value);
        if (!Number.isFinite(quantity) || quantity <= 0) {
            toast.error("Invalid quantity");
            return;
        }
        const _line = (cart?.lines || []).find((l) => l.id === lineId);
        const _ppb = Number(_line?.packaging_quantity || 0);
        const _byBox = _line && /TILE|MOROCCAN|VINYL|CUSTOMIZED MOSAIC/.test(`${_line.item_group_name || ""} ${_line.item_group || ""} ${_line.item_name || ""} ${_line.description || ""}`.toUpperCase());
        if (_byBox && _ppb > 0) {
            const _r = Math.ceil(quantity / _ppb) * _ppb;
            if (_r !== quantity) { toast.info("Sold by full box. Rounded up to the nearest full box."); quantity = _r; }
        }
        try {
            const { data } = await api.put(`/cart/items/${lineId}`, { quantity });
            setCart(data);
            toast.success("Quantity updated. Click Revalidate before converting.");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const updateGivenPrice = async (lineId, value) => {
        const givenPrice = Number(value);

        if (!Number.isFinite(givenPrice) || givenPrice < 0) {
            toast.error("Invalid given price");
            return;
        }

        try {
            const { data } = await api.put(`/cart/items/${lineId}`, {
                given_price: givenPrice,
            });
            setCart(data);
            toast.success("Given price updated");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };


    const validate = async () => {
        setBusy(true);

        try {
            const { data } = await api.post("/cart/validate");
            setCart(data);
            toast.success(`Cart status: ${data.status}`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const createCustomer = async () => {
        const companyName = newCustomer.company_name.trim();

        if (!companyName) {
            return toast.error("Company Name is required");
        }

        setBusy(true);

        try {
            const payload = {
                name: companyName,
                company_name: companyName,
                address: newCustomer.address.trim(),
                tin: newCustomer.tin.trim(),
                contact_person: newCustomer.contact_person.trim(),
                contact_no: newCustomer.contact_no.trim(),
                phone: newCustomer.contact_no.trim(),
                email: newCustomer.email.trim() || null,
                schema_name: customerContext?.customer_schema_name || "",
            };

            const { data } = await api.post("/customers", payload);

            toast.success(`Customer ${data.customer_code} created locally`);
            setNewCustomer(emptyCustomerForm);
            setShowNewCustomer(false);
            await refresh(data.customer_code);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    // Add a new client (name only) as a CRM lead owned by the agent, so it appears in the picker.
    const createClientLead = async () => {
        const name = newClientName.trim();
        if (!name) return toast.error("Enter a client name");
        setBusy(true);
        try {
            const { data } = await api.post("/crm/leads", { company_name: name });
            toast.success(`Client "${name}" added to your leads`);
            setNewClientName("");
            setShowNewClient(false);
            await refresh(data?.id);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const convert = async () => {
        if (!isNonOrganic && !customer) {
            return toast.error(customerContext?.cross_supplier_sale ? "Select an Organic client / end customer first" : "Select a customer first");
        }

        if (isNonOrganic && !user?.assigned_company_customer_code) {
            return toast.error("No company customer assigned to your user account");
        }

        setBusy(true);

        try {
            const { data } = await api.post("/cart/convert-to-quotation", {
                customer_code: isNonOrganic ? null : customer,
                line_ids: (!isNonOrganic && selectedLines.size) ? [...selectedLines] : undefined,
            });

            toast.success(`Quotation ${data.doc_no} created`);
            nav(`/quotations/${data.id}`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    // Organic "Save as draft": create a draft quotation but KEEP the cart active so the agent keeps shopping.
    const saveDraft = async () => {
        if (!customer) {
            return toast.error(customerContext?.cross_supplier_sale ? "Select an Organic client / end customer first" : "Select a customer first");
        }
        setBusy(true);
        try {
            const { data } = await api.post("/cart/convert-to-quotation", {
                customer_code: customer,
                keep_cart: true,
                line_ids: selectedLines.size ? [...selectedLines] : undefined,
            });
            toast.success(`Draft ${data.doc_no} saved — cart kept`, {
                action: { label: "Open draft", onClick: () => nav(`/quotations/${data.id}`) },
            });
            await refresh(customer);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    // Non-Organic: create a PO/order straight from the cart (no quotation).
    const createOrder = async () => {
        if (!user?.assigned_company_customer_code) {
            return toast.error("No company customer assigned to your user account");
        }
        const lines = (cart?.lines || []).map((l) => ({
            item_code: l.item_code,
            warehouse_code: l.warehouse_code,
            quantity: Number(l.quantity || 0),
            // r33: import price = current PROMO price when one is active, else the tier price
            // (distributor/dealers/SRP2). Non-organic dealers buy at promo/tier, not a markup.
            unit_price: Number((Number(l.promo_price) > 0) ? l.promo_price : linePrice(l)),
            item_name: l.item_name,
            // Carry the required tonality so SAP import allocation honors it (else it auto-picks).
            batch_code: l.batch_code || l.requested_tonality || undefined,
            // E50/E83: Sales Agent is captured per row (falls back to the order-level default).
            sales_agent: (lineAgents[l.id] ?? poFields.sales_agent ?? "") || undefined,
            // Provenance: this line came from an approved Lead Time / ETA request, so the
            // order posts as Advance PO (GL 400004) even if the item is back in stock now.
            from_lead_time_eta: l.from_lead_time_eta || undefined,
            // Both were being dropped here (2026-09-29 fix): source_inquiry_request_doc_no lets an
            // approver see which Lead Time/ETA request this line came from, and po_cutoff_date is
            // what _post_and_complete's PO-cutoff safety check reads -- without it that check was
            // dead code, since every order line ever reaching it had an empty cutoff.
            source_inquiry_request_doc_no: l.source_inquiry_request_doc_no || undefined,
            po_cutoff_date: l.po_cutoff_date || undefined,
        }));
        if (!lines.length) return toast.error("Cart is empty");
        const schema_name = cart?.lines?.[0]?.schema_name;
        const _subtotal = (cart?.lines || []).reduce((a, l) => a + Number(l.net_total ?? 0), 0);
        // Above the threshold (and not ULYMAR) -> ask about the Credit Line in a MODAL; otherwise post.
        // UAT 10-28: the credit-line prompt is suppressed for ULYMAR, and for specific users whose
        // PO posting must never offer it.
        const _noCreditLine = user?.assigned_company_customer_code === "ULYMAR"
            || ["euniceannmercado@gmail.com", "mercadomaloua@gmail.com"].includes((user?.email || "").toLowerCase());
        if (_subtotal >= 100000 && !_noCreditLine) {
            setCreditModal({ subtotal: _subtotal, lines, schema_name });
            return;
        }
        await placeOrder(lines, schema_name, false);
    };

    const placeOrder = async (lines, schema_name, use_credit_line) => {
        setCreditModal(null);
        setBusy(true);
        try {
            const { data } = await api.post("/non-organic/orders", { schema_name, lines, sales_agent: poFields.sales_agent, remarks: poFields.remarks, replacement_po_no: poFields.replacement_po_no, po_no: poFields.po_no, use_credit_line });
            toast.success(`Order ${data.doc_no} created`);
            nav(`/non-organic/orders`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const summary = useMemo(() => {
        const lines = cart?.lines || [];

        return {
            qty: cart?.total_quantity ?? lines.reduce((a, l) => a + Number(l.quantity || 0), 0),
            weight: cart?.total_weight ?? lines.reduce((a, l) => a + Number(l.total_weight || 0), 0),
            boxes: cart?.estimated_boxes ?? lines.reduce((a, l) => a + Number(l.estimated_boxes || 0), 0),
            amount: isNonOrganic
                ? lines.reduce((a, l) => a + (((Number(l.promo_price) > 0) ? Number(l.promo_price) : linePrice(l)) * Number(l.quantity || 0)), 0)
                : (cart?.subtotal ?? lines.reduce((a, l) => a + Number(l.net_total || 0), 0)),
        };
    }, [cart]);

    if (!cart) return <div className="p-6">Loading…</div>;

    return (
        <div className="p-3 md:p-6 space-y-5 md:space-y-6" data-testid="cart-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="sc-section-title">Sales Cart</div>
                    <h1 className="text-xl md:text-2xl font-semibold tracking-tight">
                        Pre-quotation workspace
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Inquiry snapshots are preserved here before creating the quotation.
                    </p>
                </div>

                <div className="flex items-center gap-2">
                    {cart.lines.length > 0 && (
                        <button
                            type="button"
                            onClick={() => { setAddOpen(true); setAddResults([]); setAddQ(""); }}
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-primary text-primary-foreground text-sm font-medium"
                            data-testid="cart-add-item"
                        >
                            <Plus className="w-4 h-4" /> Add Item
                        </button>
                    )}
                    <StatusBadge status={cart.status} />
                </div>
            </div>

            {creditModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setCreditModal(null)}>
                    <div className="sc-card w-full max-w-md p-5 space-y-4 bg-background" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <div className="font-semibold text-lg">Credit Line</div>
                            <button type="button" onClick={() => setCreditModal(null)} className="h-8 w-8 grid place-items-center rounded hover:bg-secondary"><X className="w-4 h-4" /></button>
                        </div>
                        <p className="text-sm text-muted-foreground">
                            {showPrices
                                ? `This PO is ₱${money(creditModal.subtotal)} (₱100,000 or more).`
                                : "This order is eligible for the Credit Line."}
                        </p>
                        <p className="text-sm font-medium">Would you like to use your Credit Line?</p>
                        <div className="flex items-center justify-end gap-2 pt-1">
                            <button type="button" disabled={busy} onClick={() => placeOrder(creditModal.lines, creditModal.schema_name, false)} className="h-9 px-3 rounded-md border border-border text-sm disabled:opacity-50" data-testid="credit-pay-terms">Pay with RPO Terms</button>
                            <button type="button" disabled={busy} onClick={() => placeOrder(creditModal.lines, creditModal.schema_name, true)} className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50" data-testid="credit-use-line">Use credit line</button>
                        </div>
                    </div>
                </div>
            )}

            {addOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto" onClick={() => setAddOpen(false)}>
                    <div className="sc-card w-full max-w-2xl mt-10 p-4 space-y-3 bg-background" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <div className="font-semibold">Add Item to Cart</div>
                            <button type="button" onClick={() => setAddOpen(false)} className="h-8 w-8 grid place-items-center rounded hover:bg-secondary"><X className="w-4 h-4" /></button>
                        </div>
                        <p className="text-xs text-muted-foreground">Same company and warehouse as the cart. Search an item, enter a quantity, and add it — no need to go back to Inquiry.</p>
                        <form onSubmit={(e) => { e.preventDefault(); searchAddItems(addQ); }} className="flex items-center gap-2">
                            <div className="relative flex-1">
                                <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <input value={addQ} onChange={(e) => setAddQ(e.target.value)} placeholder="Item code, name or description" className="sc-input h-9 pl-8 w-full" data-testid="cart-add-search" autoFocus />
                            </div>
                            <button type="submit" disabled={addSearching} className="h-9 px-3 rounded-md border border-border text-sm disabled:opacity-50">{addSearching ? "Searching…" : "Search"}</button>
                        </form>
                        <div className="max-h-[50vh] overflow-y-auto divide-y divide-border">
                            {addResults.length === 0 ? (
                                <div className="text-sm text-muted-foreground py-6 text-center">{addSearching ? "Searching…" : "No results yet — search for an item above."}</div>
                            ) : addResults.map((r) => (
                                <div key={r.item_code} className="py-2 flex items-center gap-3 text-sm">
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium truncate">{r.item_name || r.name || r.item_code}</div>
                                        <div className="text-xs text-muted-foreground font-mono">{r.item_code}{r.size ? ` · ${r.size}` : ""}{r.no_stock ? " · out of stock (advance PO)" : ""}</div>
                                    </div>
                                    <input type="number" min="0" step="1" value={addQty[r.item_code] ?? ""} onChange={(e) => setAddQty((pr) => ({ ...pr, [r.item_code]: e.target.value }))} placeholder="Qty" className="sc-input h-8 w-24 text-right text-xs" />
                                    <button type="button" disabled={addBusy} onClick={() => addItemToCart(r)} className="h-8 px-3 rounded-md bg-primary text-primary-foreground text-xs font-medium disabled:opacity-50">Add</button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {cart.lines.length > 0 && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <SummaryCard icon={PackageCheck} label="Total Qty" value={num(summary.qty, 0)} />
                    <SummaryCard icon={Boxes} label="Estimated Boxes" value={num(summary.boxes, 2)} />
                    <SummaryCard icon={Scale} label="Total Weight" value={`${num(summary.weight, 2)} kg`} />
                    <SummaryCard label="Cart Amount" value={showPrices ? `₱${money(summary.amount)}` : "—"} />
                </div>
            )}

            <div className="sc-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full sc-table min-w-[1050px]">
                        <thead>
                            <tr>
                                {!isNonOrganic && (
                                    <th className="w-8">
                                        <input
                                            type="checkbox"
                                            checked={cart.lines.length > 0 && selectedLines.size === cart.lines.length}
                                            onChange={(e) => setSelectedLines(e.target.checked ? new Set(cart.lines.map((x) => x.id)) : new Set())}
                                            title="Select all items for the quotation"
                                            data-testid="cart-select-all"
                                        />
                                    </th>
                                )}
                                <th>Item</th>
                                <th>Description</th>
                                <th className="text-center">Size</th>
                                <th>Schema / Wh</th>
                                <th className="text-right">Qty</th>
                                <th className="text-right">Boxes</th>
                                <th className="text-right">Pcs</th>
                                <th className="text-right">Weight</th>
                                <th>Availability</th>
                                <th>ETA</th>
                                <th className="text-right">{showPrices ? priceLabel : ""}</th>
                                {!isNonOrganic && <th className="text-right">Given Price</th>}
                                <th className="text-right">{showPrices ? "Line Total" : ""}</th>
                                <th></th>
                            </tr>
                        </thead>

                        <tbody>
                            {cart.lines.length === 0 && (
                                <tr>
                                    <td colSpan={!isNonOrganic ? 15 : 13} className="text-center text-muted-foreground py-10">
                                        Cart is empty. Use Inquiry to add items.
                                    </td>
                                </tr>
                            )}

                            {cart.lines.map((l) => (
                                <tr key={l.id} data-testid={`cart-line-${l.id}`}>
                                    {!isNonOrganic && (
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={selectedLines.has(l.id)}
                                                onChange={(e) => setSelectedLines((prev) => {
                                                    const n = new Set(prev);
                                                    if (e.target.checked) n.add(l.id); else n.delete(l.id);
                                                    return n;
                                                })}
                                                data-testid={`cart-select-${l.id}`}
                                            />
                                        </td>
                                    )}
                                    <td>
                                        <div className="font-mono text-xs">{l.item_code}</div>
                                        <div className="text-xs text-muted-foreground">{l.item_name}</div>
                                        {isNonOrganic && (
                                            <div className="mt-1.5">
                                                <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">Sales Agent (this row)</div>
                                                <input
                                                    className="sc-input h-8 text-xs w-48 mt-0.5"
                                                    placeholder="Sales agent name"
                                                    value={lineAgents[l.id] ?? poFields.sales_agent ?? ""}
                                                    onChange={(e) => setLineAgents((m) => ({ ...m, [l.id]: e.target.value }))}
                                                    data-testid={`cart-line-agent-${l.id}`}
                                                />
                                            </div>
                                        )}
                                    </td>

                                    <td className="text-xs text-muted-foreground max-w-[240px]">{l.description || "—"}</td>
                                    <td className="text-xs text-center whitespace-nowrap">{l.size || "—"}</td>

                                    <td>
                                        <div className="font-mono text-xs">{l.schema_name || "—"}</div>
                                        <div className="font-mono text-xs text-muted-foreground">
                                            {l.warehouse_code}
                                        </div>
                                    </td>

                                    <td>
                                        <input
                                            type="number"
                                            min="1"
                                            step="1"
                                            defaultValue={Number(l.quantity || 0)}
                                            onBlur={(e) => updateQuantity(l.id, e.target.value)}
                                            className="sc-input h-8 text-right font-mono text-xs w-24"
                                            data-testid={`cart-line-quantity-${l.id}`}
                                        />
                                    </td>
                                    <td className="sc-num">{(() => {
                                        const ppb = Number(l.packaging_quantity || 0);
                                        const qty = Number(l.quantity || 0);
                                        const byBox = /TILE|MOROCCAN|VINYL|CUSTOMIZED MOSAIC/.test(`${l.item_group_name || ""} ${l.item_group || ""} ${l.item_name || ""} ${l.description || ""}`.toUpperCase());
                                        if (byBox && ppb > 0) {
                                            const b = Math.floor(qty / ppb);
                                            const pc = Math.round(qty - b * ppb);
                                            return pc > 0 ? `${b} box + ${pc} pc` : `${b} box`;
                                        }
                                        return byBox ? num(l.estimated_boxes, 2) : "—";
                                    })()}</td>
                                    <td className="sc-num">{(() => {
                                        const byBox = /TILE|MOROCCAN|VINYL|CUSTOMIZED MOSAIC/.test(`${l.item_group_name || ""} ${l.item_group || ""} ${l.item_name || ""} ${l.description || ""}`.toUpperCase());
                                        const ppb = Number(l.packaging_quantity || 0);
                                        const qty = Number(l.quantity || 0);
                                        if (!byBox) return num(qty, 0);
                                        return ppb ? num(qty - Math.floor(qty / ppb) * ppb, 0) : "—";
                                    })()}</td>
                                    <td className="sc-num">
                                        {num(l.total_weight, 2)} {l.weight_unit || "kg"}
                                    </td>

                                    <td>
                                        <StatusBadge status={l.availability_status || l.allocation?.status} />
                                        <div className="text-[11px] text-muted-foreground mt-1">
                                            {statusText(l)}
                                        </div>
                                        {l.requested_tonality && (
                                            <div className="text-[11px] font-mono text-muted-foreground mt-1">
                                                Tonality: {l.requested_tonality}
                                            </div>
                                        )}
                                    </td>

                                    <td className="text-xs text-muted-foreground">
                                        {l.eta || (l.advanced_po_allowed ? "Pending Purchasing review" : "—")}
                                    </td>

                                    <td className="sc-num">
                                        {showPrices ? (<>
                                        ₱{money(linePrice(l))}
                                        {showLinePromo(l) ? (
                                            <div className="text-sm font-bold text-red-600 mt-0.5">Promo ₱{money(l.promo_price)}</div>
                                        ) : null}
                                        </>) : <span className="text-muted-foreground">—</span>}
                                    </td>

                                    {!isNonOrganic && (
                                    <td>
                                        {showPrices ? (
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            defaultValue={Number(l.given_price ?? l.unit_price ?? 0)}
                                            onBlur={(e) => updateGivenPrice(l.id, e.target.value)}
                                            className="sc-input h-8 text-right font-mono text-xs"
                                            data-testid={`cart-line-given-price-${l.id}`}
                                        />
                                        ) : <span className="text-muted-foreground">—</span>}
                                    </td>
                                    )}

                                    <td className="sc-num">
                                        {!showPrices ? <span className="text-muted-foreground">—</span>
                                         : isNonOrganic ? <>₱{money(npLineTotal(l))}</>
                                         : (<>
                                        ₱{money(l.net_total)}

                                        {l.price_override && (
                                            <div className="text-[11px] text-amber-600 mt-1">
                                                Override
                                            </div>
                                        )}
                                        </>)}
                                    </td>

                                    <td className="text-right">
                                        <button
                                            onClick={() => removeLine(l.id)}
                                            className="p-1 text-muted-foreground hover:text-red-600"
                                            data-testid={`cart-line-remove-${l.id}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}

                            {cart.lines.length > 0 && (
                                <tr className="bg-secondary/40">
                                    <td colSpan={11} className="text-right text-xs uppercase tracking-wider text-muted-foreground font-medium">
                                        Subtotal
                                    </td>
                                    <td className="sc-num font-semibold" data-testid="cart-subtotal">
                                        {showPrices ? `₱${money(isNonOrganic ? (cart.lines || []).reduce((a, l) => a + npLineTotal(l), 0) : cart.subtotal)}` : "—"}
                                    </td>
                                    <td></td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {cart.lines.length > 0 && (
                <div className="sc-card p-4 space-y-4">
                    <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 lg:items-end">
                        <div>
                            <div className="flex items-center justify-between gap-3">
                                <label className="text-xs font-medium text-muted-foreground">
                                    {customerContext?.cross_supplier_sale ? "Client / End Customer" : "Customer"}
                                </label>

                                {!isNonOrganic && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-[11px] text-muted-foreground">From your CRM leads</span>
                                        {customer && (
                                            <button
                                                type="button"
                                                onClick={() => setClientModal({ mode: "edit", id: customer })}
                                                className="h-7 inline-flex items-center gap-1 px-2 rounded-md border border-border text-xs hover:bg-secondary"
                                                data-testid="cart-edit-client"
                                            >
                                                <Pencil className="w-3 h-3" /> Edit client
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => setClientModal({ mode: "new" })}
                                            className="h-7 inline-flex items-center gap-1 px-2 rounded-md border border-border text-xs hover:bg-secondary"
                                            data-testid="cart-new-client-toggle"
                                        >
                                            <Plus className="w-3 h-3" /> New client
                                        </button>
                                    </div>
                                )}
                            </div>

                            <select
                                className="sc-input mt-1"
                                value={customer}
                                onChange={(e) => setCustomer(e.target.value)}
                                data-testid="cart-customer"
                            >
                                {customers.length === 0 && (
                                    <option value="">No leads found — add leads in the CRM module</option>
                                )}

                                {customers.map((c) => (
                                    <option key={c.customer_code} value={c.customer_code}>
                                        {c.company_name || c.label || c.name}{c.contact ? ` · ${c.contact}` : ""}
                                    </option>
                                ))}
                            </select>

                            {customerContext?.cross_supplier_sale && (
                                <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                                    <div>
                                        <span className="font-medium">Customer / Sold-To:</span>{" "}
                                        {customerContext.company_customer_name || user?.company || "Organic company"}
                                        {customerContext.company_customer_code ? ` (${customerContext.company_customer_code})` : ""}
                                    </div>
                                    <div>
                                        <span className="font-medium">Client source:</span>{" "}
                                        {customerContext.customer_schema_display_name || customerContext.customer_schema_name || "Organic schema"}
                                    </div>
                                    <div>
                                        <span className="font-medium">Inventory source:</span>{" "}
                                        {customerContext.source_schema_display_name || customerContext.source_schema_name}
                                    </div>
                                </div>
                            )}
                        </div>

                        {isNonOrganic && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 mb-2">
                                <div>
                                    <label className="text-xs text-muted-foreground">Store PO No.</label>
                                    <input className="sc-input mt-1 h-9" value={poFields.po_no} onChange={(e) => setPoFields((f) => ({ ...f, po_no: e.target.value }))} placeholder="Store PO number" />
                                </div>
                                <div>
                                    <label className="text-xs text-muted-foreground">Sales Agent (default for all rows)</label>
                                    <input className="sc-input mt-1 h-9" list="sap-sales-agents" value={poFields.sales_agent} onChange={(e) => setPoFields((f) => ({ ...f, sales_agent: e.target.value }))} placeholder="Type or pick from SAP" />
                                    <datalist id="sap-sales-agents">
                                        {agentOptions.map((a) => <option key={a} value={a} />)}
                                    </datalist>
                                </div>
                                <div>
                                    <label className="text-xs text-muted-foreground">Replacement to PO No.</label>
                                    <input className="sc-input mt-1 h-9" value={poFields.replacement_po_no} onChange={(e) => setPoFields((f) => ({ ...f, replacement_po_no: e.target.value }))} placeholder="Optional" />
                                </div>
                                <div>
                                    <label className="text-xs text-muted-foreground">Remarks</label>
                                    <input className="sc-input mt-1 h-9" value={poFields.remarks} onChange={(e) => setPoFields((f) => ({ ...f, remarks: e.target.value }))} placeholder="Optional" />
                                </div>
                            </div>
                        )}

                        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2">
                            <button
                                onClick={validate}
                                disabled={busy}
                                className="h-10 inline-flex items-center justify-center gap-2 px-3 rounded-md border border-border text-sm hover:bg-secondary disabled:opacity-50 whitespace-nowrap"
                                data-testid="cart-validate"
                            >
                                <RefreshCcw className="w-4 h-4" />
                                Revalidate
                            </button>

                            {!isNonOrganic && (
                                <button
                                    onClick={saveDraft}
                                    disabled={busy}
                                    className="h-10 inline-flex items-center justify-center gap-2 px-3 rounded-md border border-border text-sm hover:bg-secondary disabled:opacity-50 whitespace-nowrap"
                                    data-testid="cart-save-draft"
                                >
                                    Save as draft
                                </button>
                            )}

                            <button
                                onClick={isNonOrganic ? createOrder : convert}
                                disabled={busy}
                                className="h-10 inline-flex items-center justify-center gap-2 px-4 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
                                data-testid="cart-convert"
                            >
                                {isNonOrganic ? "Create Order / PO" : "Convert to Quotation"}
                                <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {showNewCustomer && !isNonOrganic && (
                        <div className="rounded-md border border-border bg-secondary/20 p-4">
                            <div className="font-medium text-sm mb-3">Create Local Customer</div>

                            <LeadPicker
                                onPick={(l) => setNewCustomer({
                                    company_name: l.company_name || "",
                                    tin: "",
                                    contact_person: `${l.first_name || ""} ${l.last_name || ""}`.trim(),
                                    contact_no: l.phone || l.mobile || "",
                                    email: l.email || "",
                                    address: l.address || "",
                                })}
                            />

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <Field label="Company Name" required value={newCustomer.company_name} onChange={(v) => setNewCustomer({ ...newCustomer, company_name: v })} />
                                <Field label="TIN" value={newCustomer.tin} onChange={(v) => setNewCustomer({ ...newCustomer, tin: v })} />
                                <Field label="Contact Person" value={newCustomer.contact_person} onChange={(v) => setNewCustomer({ ...newCustomer, contact_person: v })} />
                                <Field label="Contact No." value={newCustomer.contact_no} onChange={(v) => setNewCustomer({ ...newCustomer, contact_no: v })} />
                                <Field label="Email Address" type="email" value={newCustomer.email} onChange={(v) => setNewCustomer({ ...newCustomer, email: v })} />
                                <Field label="Address" value={newCustomer.address} onChange={(v) => setNewCustomer({ ...newCustomer, address: v })} />
                            </div>

                            <div className="mt-3 flex justify-end">
                                <button
                                    type="button"
                                    onClick={createCustomer}
                                    disabled={busy}
                                    className="h-9 inline-flex items-center justify-center gap-2 px-4 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 disabled:opacity-50"
                                >
                                    Save Local Customer
                                </button>
                            </div>

                            <div className="text-xs text-muted-foreground mt-2">
                                This creates a Sale Hub-only customer. It will not write to SAP.
                            </div>
                        </div>
                    )}
                </div>
            )}
            {clientModal && (
                <ClientModal
                    spec={clientModal}
                    onClose={() => setClientModal(null)}
                    onSaved={(id) => { setClientModal(null); refresh(id); }}
                />
            )}
        </div>
    );
}

// New / Edit a client (a CRM lead) with full customer details, from the cart.
function ClientModal({ spec, onClose, onSaved }) {
    const [form, setForm] = useState({ company_name: "", first_name: "", mobile: "", email: "", address: "", title: "" });
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (spec.mode === "edit" && spec.id) {
            api.get(`/crm/leads/${spec.id}`).then(({ data }) => setForm({
                company_name: data.company_name || "",
                first_name: data.first_name || "",
                mobile: data.mobile || data.phone || "",
                email: data.email || "",
                address: data.address || "",
                title: data.title || "",
            })).catch(() => {});
        }
    }, [spec]);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const save = async () => {
        if (!form.company_name.trim() && !form.first_name.trim()) {
            return toast.error("Company name or contact person is required");
        }
        setSaving(true);
        try {
            const body = {
                company_name: form.company_name, first_name: form.first_name, mobile: form.mobile,
                email: form.email, address: form.address, title: form.title,
            };
            let id;
            if (spec.mode === "edit") {
                await api.put(`/crm/leads/${spec.id}`, body); id = spec.id; toast.success("Client details updated");
            } else {
                const { data } = await api.post("/crm/leads", body); id = data.id; toast.success("Client added");
            }
            onSaved(id);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };
    return (
        <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={onClose}>
            <div className="sc-card bg-background w-full max-w-lg p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="cart-client-modal">
                <div className="text-lg font-semibold">{spec.mode === "edit" ? "Edit Client" : "New Client"}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className="text-sm block md:col-span-2"><span className="text-muted-foreground text-xs">Company Name</span>
                        <input className="sc-input mt-1" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="client-company" /></label>
                    <label className="text-sm block"><span className="text-muted-foreground text-xs">Contact Person</span>
                        <input className="sc-input mt-1" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} /></label>
                    <label className="text-sm block"><span className="text-muted-foreground text-xs">Contact No.</span>
                        <input className="sc-input mt-1" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} /></label>
                    <label className="text-sm block"><span className="text-muted-foreground text-xs">Email</span>
                        <input className="sc-input mt-1" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} /></label>
                    <label className="text-sm block"><span className="text-muted-foreground text-xs">Title</span>
                        <input className="sc-input mt-1" value={form.title} onChange={(e) => set("title", e.target.value)} /></label>
                    <label className="text-sm block md:col-span-2"><span className="text-muted-foreground text-xs">Address</span>
                        <input className="sc-input mt-1" value={form.address} onChange={(e) => set("address", e.target.value)} /></label>
                </div>
                <div className="flex justify-end gap-2">
                    <button onClick={onClose} className="h-9 px-3 rounded border border-border text-sm">Cancel</button>
                    <button onClick={save} disabled={saving} className="h-9 px-4 rounded bg-foreground text-background text-sm disabled:opacity-50" data-testid="client-save">{saving ? "Saving…" : "Save"}</button>
                </div>
            </div>
        </div>
    );
}

// Search the current user's OWN leads (the /crm/leads endpoint is scope-enforced:
// an agent sees only their leads, admin sees all) and auto-fill the customer form.
function LeadPicker({ onPick }) {
    const [q, setQ] = useState("");
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [picked, setPicked] = useState(null);

    useEffect(() => {
        const term = q.trim();
        if (picked && term === picked) return;   // don't re-search right after a pick
        if (term.length < 2) { setRows([]); setOpen(false); return; }
        let alive = true;
        setLoading(true);
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get("/crm/leads", { params: { q: term, limit: 8 } });
                if (alive) { setRows(data?.items || []); setOpen(true); }
            } catch { /* ignore */ } finally { if (alive) setLoading(false); }
        }, 300);
        return () => { alive = false; clearTimeout(t); };
    }, [q, picked]);

    const label = (l) => `${l.first_name || ""} ${l.last_name || ""}`.trim() || l.company_name || "Lead";

    return (
        <div className="relative mb-3">
            <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5">
                <UserCheck className="w-3.5 h-3.5" /> Search from my leads
            </div>
            <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                    className="sc-input pl-9"
                    placeholder="Type a company or contact name to auto-fill…"
                    value={q}
                    onChange={(e) => { setQ(e.target.value); setPicked(null); }}
                    onFocus={() => rows.length && setOpen(true)}
                    data-testid="cart-lead-search"
                />
                {loading && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">…</span>}
            </div>
            {open && rows.length > 0 && (
                <div className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto rounded-md border border-border bg-background shadow-lg">
                    {rows.map((l) => (
                        <button
                            key={l.id}
                            type="button"
                            onClick={() => { onPick(l); setPicked(label(l)); setQ(label(l)); setOpen(false); }}
                            className="w-full text-left px-3 py-2 hover:bg-secondary/60 border-b border-border last:border-0"
                        >
                            <div className="text-sm font-medium">{label(l)}</div>
                            <div className="text-xs text-muted-foreground truncate">
                                {[l.company_name, l.phone || l.mobile, l.email].filter(Boolean).join(" · ") || "no contact details"}
                                {l.store ? ` — ${l.store}` : ""}
                            </div>
                        </button>
                    ))}
                </div>
            )}
            {picked && (
                <div className="text-[11px] text-emerald-600 mt-1 flex items-center gap-1">
                    <UserCheck className="w-3 h-3" /> Filled from lead: {picked}
                </div>
            )}
        </div>
    );
}

function Field({ label, value, onChange, type = "text", required = false }) {
    return (
        <label className="block">
            <div className="text-xs text-muted-foreground mb-1">
                {label}{required ? " *" : ""}
            </div>
            <input
                type={type}
                className="sc-input"
                value={value}
                onChange={(e) => onChange(e.target.value)}
            />
        </label>
    );
}

function SummaryCard({ icon: Icon, label, value }) {
    return (
        <div className="sc-card p-4">
            <div className="flex items-center justify-between gap-2">
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        {label}
                    </div>
                    <div className="text-lg font-semibold mt-1">{value}</div>
                </div>

                {Icon && <Icon className="w-5 h-5 text-muted-foreground" />}
            </div>
        </div>
    );
}
